<script setup lang="ts">
import { ref, onMounted, useTemplateRef } from 'vue'
import type { Ref } from 'vue'
// to make it work in Safari 16 and 17 we need legacy bundle https://github.com/mozilla/pdf.js/wiki/Frequently-Asked-Questions#faq-support
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf'
import type { PDFDocumentProxy, PageViewport } from 'pdfjs-dist'
import { throttle } from '~/utils'
import DangerAlert from '~/components/alerts/DangerAlert.vue'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

interface Props {
  fileUrl: string
}
const props = defineProps<Props>()

let pdf: PDFDocumentProxy | null = null
const pdfCanvas = useTemplateRef('pdfCanvas')
const totalPages = ref(0)
const pageNumber = ref(1)
const errorWhileLoading: Ref<boolean> = ref(false)
const pdfIsLoading: Ref<boolean> = ref(false)
const transitionClass: Ref<string> = ref('')
const pageIsBeingRendered: Ref<boolean> = ref(false)
const { $sentryCaptureException, $label } = useNuxtApp()

const showPrevArrow = computed(
  () => !errorWhileLoading.value && pageNumber.value > 1
)
const showNextArrow = computed(
  () => !errorWhileLoading.value && pageNumber.value < totalPages.value
)

type ScrollDirection = 'prev' | 'next'
function addPageTransition(direction: ScrollDirection) {
  transitionClass.value = `viewer-transition--${direction}`

  setTimeout(() => {
    transitionClass.value = ''
  }, 550)
}

function onResize() {
  throttle(() => {
    setTimeout(() => renderPage(pageNumber.value), 10)
  }, 300)
}

function getScale(viewport: PageViewport): number {
  const modalWidth =
    document.querySelector('[id="ebookPreview"] .modal-body')?.clientWidth ||
    window.innerWidth
  let widthPercentage = 0.9

  if (window.innerWidth > 768) {
    widthPercentage = 0.6
  }

  if (window.innerWidth > 992) {
    widthPercentage = 0.45
  }

  return (modalWidth * widthPercentage) / viewport.width
}

async function renderPage(pageNum: number) {
  const canvas = pdfCanvas.value

  if (!pdf || !canvas) return

  pageIsBeingRendered.value = true

  const context = canvas.getContext('2d')

  if (context) {
    const page = await pdf.getPage(pageNum)
    const viewport = page.getViewport({ scale: 1 })
    const scale = getScale(viewport)
    const scaledViewport = page.getViewport({ scale })
    canvas.width = Math.floor(scaledViewport.width)
    canvas.height = Math.floor(scaledViewport.height)
    canvas.style.width = Math.floor(scaledViewport.width) + 'px'
    canvas.style.height = Math.floor(scaledViewport.height) + 'px'

    const renderContext = {
      canvasContext: context,
      viewport: scaledViewport
    }

    await page.render(renderContext).promise

    pdfIsLoading.value = false
    pageIsBeingRendered.value = false
  }
}

async function loadPdf(): Promise<void> {
  try {
    pdfIsLoading.value = true
    const loadingTask = pdfjs.getDocument(props.fileUrl)
    pdf = await loadingTask.promise

    if (pdf) {
      totalPages.value = pdf.numPages
      await renderPage(pageNumber.value)
    }
  } catch (error) {
    errorWhileLoading.value = true
    pdfIsLoading.value = false
    $sentryCaptureException(error)
  }
}

async function onNextPage() {
  if (!showNextArrow.value || pageIsBeingRendered.value) {
    return
  }
  pageNumber.value = pageNumber.value + 1
  addPageTransition('next')
  await renderPage(pageNumber.value)
}

async function onPrevPage() {
  if (!showPrevArrow.value || pageIsBeingRendered.value) {
    return
  }
  pageNumber.value = pageNumber.value - 1
  addPageTransition('prev')
  await renderPage(pageNumber.value)
}

async function keyListener(event: KeyboardEvent): Promise<void> {
  if (event.key === 'ArrowLeft') {
    await onPrevPage()
  }

  if (event.key === 'ArrowRight') {
    await onNextPage()
  }
}

onMounted(async () => {
  await loadPdf()
  window.addEventListener('resize', onResize)
  document.addEventListener('keydown', keyListener, false)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
  document.removeEventListener('keydown', keyListener, false)
})
</script>

<template>
  <div v-if="pdfIsLoading" class="spinner-wrapper">
    <BaseSpinner classes="spinner__icon text-soft" type="grow" />
  </div>

  <div :class="['w-100 grid-container', transitionClass]">
    <div v-if="showPrevArrow" class="arrow arrow--prev" @click="onPrevPage">
      <span ariant="link" class="arrow__icon arrow__icon--prev">‹</span>
    </div>
    <DangerAlert v-if="errorWhileLoading">
      {{ $label('messages.errorOccuredMessage', undefined) }}
    </DangerAlert>
    <BaseSlider
      v-if="!errorWhileLoading"
      class="pdf-viewer-wrapper d-flex"
      :next="onNextPage"
      :prev="onPrevPage"
    >
      <canvas ref="pdfCanvas"></canvas>
    </BaseSlider>
    <div v-if="showNextArrow" class="arrow arrow--next" @click="onNextPage">
      <span variant="link" class="arrow__icon arrow__icon--next">›</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.spinner-wrapper {
  height: 42vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner__icon {
  height: 6rem;
  width: 6rem;
}

.grid-container {
  display: grid;
  grid-template-columns: 1fr;
}

canvas {
  margin: auto;
  padding-bottom: 24px;
}

.arrow {
  display: none;
  outline: none;
}

.arrow__icon {
  color: $mid-grey;
  font-family: arial, sans-serif;
  font-size: 64px;
  font-weight: $font-weight-semibold;
  position: absolute;
  text-decoration: none;
  position: relative;
  cursor: pointer;
  outline: none;
}

@include media-breakpoint-up(md) {
  .grid-container {
    display: grid;
    grid-template-columns: 1fr;
  }

  .grid-container:has(.arrow) {
    grid-template-columns: 3fr 6fr 3fr;
  }

  .pdf-viewer-wrapper {
    grid-column-start: 2;
  }

  .arrow {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
